import {
  AnalyticsGranularity,
  SearchBaseFilters,
  SearchBreakdown,
  SearchQuerySort,
} from "../../api/analyticsAdmin";
import {
  searchFunnelOverview,
  searchFunnelSeries,
  searchQueryFunnel,
  searchTrending,
} from "../../api/analyticsStream";
import { SearchFilters } from "./SearchFilterBar";

/**
 * The three breakdowns shown under the funnel. "None" is its own section because
 * it drives the funnel bars from the API's exact `all`-segment rates.
 */
export const SEARCH_BREAKDOWNS = [
  { id: "bdGender", key: "Gender", label: "Gender" },
  { id: "bdAgeBand", key: "AgeBand", label: "Age band" },
  { id: "bdTypeaheadOrigin", key: "TypeaheadOrigin", label: "Typeahead origin" },
] as const satisfies readonly {
  id: string;
  key: SearchBreakdown;
  label: string;
}[];

export const QUERY_FUNNEL_TOP_N = 50;

// "Min searches" only meaningfully affects the rate-based sorts, where a
// tiny-sample query (e.g. 1 search, 1 open = 100%) would otherwise dominate.
// For Volume sort it is not used, so it is hidden and 0 is sent.
export const sortUsesMinSearches = (sort: SearchQuerySort): boolean =>
  sort !== "Volume";

const base = (filters: SearchFilters): SearchBaseFilters => ({
  preset: filters.preset,
  ...(filters.preset === "Custom" ? { from: filters.from, to: filters.to } : {}),
  platform: filters.platform,
  audience: filters.audience,
});

export interface SearchSectionInputs {
  filters: SearchFilters;
  trendBreakdown: SearchBreakdown;
  trendGranularity: AnalyticsGranularity;
  querySort: SearchQuerySort;
  queryMinSearches: number;
  trendingGranularity: AnalyticsGranularity;
  trendingTopN: number;
}

/** Everything the Search tab asks for, as one batch. See clicksSections. */
export const searchSections = (inputs: SearchSectionInputs) => {
  const shared = base(inputs.filters);

  return {
    series: searchFunnelSeries({
      ...shared,
      breakdown: inputs.trendBreakdown,
      granularity: inputs.trendGranularity,
    }),
    overview: searchFunnelOverview({ ...shared, breakdown: "None" }),
    bdGender: searchFunnelOverview({ ...shared, breakdown: "Gender" }),
    bdAgeBand: searchFunnelOverview({ ...shared, breakdown: "AgeBand" }),
    bdTypeaheadOrigin: searchFunnelOverview({
      ...shared,
      breakdown: "TypeaheadOrigin",
    }),
    queryFunnel: searchQueryFunnel({
      ...shared,
      topN: QUERY_FUNNEL_TOP_N,
      minSearches: sortUsesMinSearches(inputs.querySort)
        ? inputs.queryMinSearches
        : 0,
      sort: inputs.querySort,
    }),
    trending: searchTrending({
      ...shared,
      granularity: inputs.trendingGranularity,
      topN: inputs.trendingTopN,
    }),
  };
};

/**
 * The Suggestions tab only needs the always-empty queries: the catalog gaps an
 * admin can close by mapping merchants to them.
 */
export const zeroResultQueriesSection = (
  filters: SearchFilters,
  minSearches: number
) =>
  searchQueryFunnel({
    ...base(filters),
    topN: QUERY_FUNNEL_TOP_N,
    minSearches,
    sort: "ZeroResults",
  });
